import type { SerializedStyles, Theme } from '@emotion/react';
import { css } from '@emotion/react';

import { breakpoint, duration, easing } from '@twistopayments/design-system/styles';

import type { BannerSize, BannerVariant } from './index';

export const container = (theme: Theme) => css`
  display: grid;
  grid-template-areas:
    'icon title arrow'
    'icon subtitle arrow';
  grid-template-columns: max-content minmax(0, 1fr) max-content;
  grid-template-rows: max-content max-content;
  column-gap: ${theme.spacing[8]};
  row-gap: ${theme.spacing[4]};
  align-items: center;
  border-radius: ${theme.presentation.surface.bannerRadius};
  word-break: break-word;
  transition: background-color ${easing.easeInOut} ${duration.standard}ms;

  & .banner-icon-wrapper {
    transition: background-color ${easing.easeInOut} ${duration.standard}ms;
  }

  ${breakpoint.up('sm')} {
    column-gap: ${theme.spacing[16]};
    row-gap: ${theme.spacing[8]};
  }
  ${theme.presentation.surface.banner}
`;

export const singleLine = css`
  grid-template-areas: 'icon title arrow';
  grid-template-rows: max-content;
`;

export const containerHover = css`
  cursor: pointer;
`;

export const containerBackground: Record<BannerVariant, (theme: Theme) => SerializedStyles> = {
  primary: (theme) => css`
    background-color: ${theme.palette.primary.lowContrast};
  `,
  secondary: (theme) => css`
    background-color: ${theme.presentation.surface.secondary};
  `,
  warning: (theme) => css`
    background-color: ${theme.palette.warning.lowContrast};
  `,
  error: (theme) => css`
    background-color: ${theme.palette.error.lowContrast};
  `,
};

export const containerSize: Record<BannerSize, (theme: Theme) => SerializedStyles> = {
  small: (theme) => css`
    padding: ${theme.spacing[12]};

    ${breakpoint.up('sm')} {
      padding: ${theme.spacing[16]};
    }
  `,
  medium: (theme) => css`
    padding: ${theme.spacing[12]};

    ${breakpoint.up('sm')} {
      padding: ${theme.spacing[24]};
    }
  `,
};

export const containerBackgroundHover: Record<BannerVariant, (theme: Theme) => SerializedStyles> = {
  primary: (theme) => css`
    &:hover {
      background-color: ${theme.palette.primary.lowerMidContrast};

      & .banner-title-highlighted {
        color: ${theme.palette.primary.highContrast};
      }
    }
  `,
  secondary: (theme) => css`
    &:hover {
      background-color: ${theme.presentation.surface.secondaryHover};
    }
  `,
  warning: (theme) => css`
    &:hover {
      background-color: ${theme.palette.warning.mediumContrast};

      & .banner-icon-wrapper {
        background-color: ${theme.palette.warning.lowContrast};
      }
    }
  `,
  error: (theme) => css`
    &:hover {
      background-color: ${theme.palette.error.mediumContrast};

      & .banner-icon-wrapper {
        background-color: ${theme.palette.error.lowContrast};
      }
    }
  `,
};

export const arrow = css`
  grid-area: arrow;
  align-self: center;
`;

export const arrowColor: Record<BannerVariant, (theme: Theme) => SerializedStyles> = {
  primary: (theme) => css`
    color: ${theme.palette.primary.mainContrast};
  `,
  secondary: (theme) => css`
    color: ${theme.palette.tertiary.highContrast};
  `,
  warning: (theme) => css`
    color: ${theme.palette.warning.main};
  `,
  error: (theme) => css`
    color: ${theme.palette.error.main};
  `,
};

export const arrowSize: Record<BannerSize, (theme: Theme) => SerializedStyles> = {
  small: (theme) => css`
    width: ${theme.spacing[24]};
    height: ${theme.spacing[24]};
  `,
  medium: (theme) => css`
    width: ${theme.spacing[24]};
    height: ${theme.spacing[24]};

    ${breakpoint.up('sm')} {
      width: ${theme.spacing[32]};
      height: ${theme.spacing[32]};
    }
  `,
};
